// hooks/useNotifications.ts — reactive hook for the notifications list.
//
// Reads from notificationStore reactively and exposes service mutations.
// Components must NOT import notificationStore or notificationsService directly.
//
// useShallow is used on any filtering/derived-list selector to prevent
// re-renders when unrelated notifications change.

import { useShallow } from 'zustand/react/shallow';
import { useNotificationStore } from '@/core/stores/notificationStore';
import { notificationsService } from '@/core/services/notificationsService';
import type { Notification, NotificationType } from '@/core/models/notification';

export interface NotificationsHook {
  /** All notifications in seed order. */
  notifications: Notification[];
  /** Number of unread notifications (numeric). */
  unreadCount: number;
  /** Mark a single notification as read. */
  markRead: (id: string) => Promise<void>;
  /** Mark all notifications as read. */
  markAllRead: () => Promise<void>;
}

export interface NotificationsByTypeHook {
  /** Notifications filtered to a specific type. */
  notifications: Notification[];
}

export interface NotificationsByScopeHook {
  /** Notifications filtered to a specific scope. */
  notifications: Notification[];
}

/** Primary hook: all notifications + read/unread mutations. */
export function useNotifications(): NotificationsHook {
  const notifications = useNotificationStore((s) => s.notifications);
  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    notifications,
    unreadCount,
    markRead: (id) => notificationsService.markRead(id),
    markAllRead: () => notificationsService.markAllRead(),
  };
}

/**
 * Filtered hook: notifications for a specific type.
 * Uses useShallow because the selector returns a filtered array (new ref each time).
 */
export function useNotificationsByType(type: NotificationType): NotificationsByTypeHook {
  const notifications = useNotificationStore(
    useShallow((s) => s.notifications.filter((n) => n.type === type)),
  );
  return { notifications };
}

/**
 * Filtered hook: notifications by scope ('all' | 'followed').
 * Uses useShallow because the selector returns a filtered array.
 */
export function useNotificationsByScope(scope: 'all' | 'followed'): NotificationsByScopeHook {
  const notifications = useNotificationStore(
    useShallow((s) =>
      scope === 'all'
        ? s.notifications
        : s.notifications.filter((n) => n.fromFollowed === true),
    ),
  );
  return { notifications };
}
